import React, { useState, useEffect } from 'react'
import { Plus, Minus, Star, Shield } from 'lucide-react'
import { SecretMenuItem, supabase } from '../../lib/supabase'
import { CalculatedEventStatus } from '../../lib/utils/eventStatus'
import { useCart } from '../../contexts/CartContext'
import { formatPrice } from '../../lib/square'
import toast from 'react-hot-toast'
import { EnterpriseToast } from '../ui/EnterpriseToast'

interface MenuItemCardProps {
  item: SecretMenuItem
  className?: string
  eventStatus?: CalculatedEventStatus
}

const MenuItemCard: React.FC<MenuItemCardProps> = ({ item, className = '', eventStatus }) => {
  const { addItem, updateQuantity, getItemQuantity, isInCart } = useCart()
  const [isAdding, setIsAdding] = useState(false)
  const [actualQuantitySold, setActualQuantitySold] = useState<number | null>(null)

  const currentQuantity = getItemQuantity(item.id)

  // Phase 3: Fetch actual quantity sold for items with available_quantity set
  useEffect(() => {
    const fetchActualQuantity = async () => {
      if (item.available_quantity !== null && item.available_quantity !== undefined) {
        try {
          const { data, error } = await supabase.rpc('get_menu_item_current_quantity', {
            p_menu_item_id: item.id
          })
          if (!error && data !== null) {
            setActualQuantitySold(data)
          }
        } catch (error) {
          console.error('Failed to fetch actual quantity:', error)
        }
      }
    }

    fetchActualQuantity()
  }, [item.id, item.available_quantity])

  // Calculate availability based on available_quantity if set, otherwise use deprecated fields
  const hasInventoryLimit = item.available_quantity !== null && item.available_quantity !== undefined
  const actualRemaining = hasInventoryLimit && actualQuantitySold !== null
    ? item.available_quantity - actualQuantitySold
    : item.max_quantity - item.current_quantity

  const isAvailable = item.is_available && actualRemaining > 0

  // Phase 3b: Determine if ordering is active
  const canOrder = eventStatus === 'Active' || eventStatus === 'In Progress'

  const handleAddToCart = async () => {
    if (!isAvailable) return

    setIsAdding(true)
    try {
      addItem(item, 1)
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="success"
          title="Added to Cart"
          message={item.name}
        />
      ))
    } catch (error) {
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Failed to Add Item"
          message="Please try again"
        />
      ))
    } finally {
      setIsAdding(false)
    }
  }

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity < 0 || newQuantity > actualRemaining) return
    updateQuantity(item.id, newQuantity)

    if (newQuantity === 0) {
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="success"
          title="Removed from Cart"
          message={item.name}
        />
      ))
    }
  }

  const truncateDescription = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength) + '...'
  }

  return (
    <div className={`flex flex-col h-full bg-card rounded-lg overflow-hidden border border-border hover:border-secret-primary/50 hover:scale-[1.02] hover:shadow-xl transition-all duration-300 ${className}`}>
      {/* Image - Fixed Aspect Ratio */}
      <div className="relative aspect-video bg-gradient-to-br from-secret-primary/20 to-secret-primary/5 overflow-hidden">
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={item.name}
            className={`w-full h-full object-cover ${eventStatus === 'Completed' ? 'grayscale-[60%]' : ''}`}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-secret-text-tertiary text-6xl">🦞</div>
          </div>
        )}

        {/* SECRET Exclusive Badge - Moved to top-right */}
        {item.is_secret_exclusive && (
          <div className="absolute top-3 right-3">
            <div className="flex items-center space-x-1 bg-secret-primary text-xs px-2 py-1 rounded-full shadow-lg">
              <Shield size={12} className="text-white" />
              <span className="font-medium text-white">SECRET</span>
            </div>
          </div>
        )}

        {/* Stock Indicator */}
        {actualRemaining <= 3 && actualRemaining > 0 && (
          <div className="absolute top-3 left-3">
            <div className="bg-orange-600 text-white text-xs px-2 py-1 rounded-full shadow-lg">
              Only {actualRemaining} left
            </div>
          </div>
        )}

        {/* Sold Out Overlay */}
        {!isAvailable && (
          <div className="absolute inset-0 bg-black/80 flex items-center justify-center">
            <div className="text-white font-bold text-lg">SOLD OUT</div>
          </div>
        )}
      </div>

      {/* Content - Flex Column with Auto-growing Space */}
      <div className="flex flex-col flex-grow p-4">
        {/* Header */}
        <div className="flex justify-between items-start mb-2">
          <h3 className="font-bold text-gray-900 dark:text-white text-lg">{item.name}</h3>
          <div className="text-secret-primary font-bold text-lg whitespace-nowrap ml-2">
            {formatPrice(item.price * 100)}
          </div>
        </div>

        {/* Description - Line Clamped */}
        {item.description && (
          <p className="text-muted-foreground text-sm mb-4 leading-relaxed line-clamp-2">
            {item.description}
          </p>
        )}

        {/* Spacer to push buttons to bottom */}
        <div className="flex-grow"></div>

        {/* Add to Cart Section - Pushed to Bottom */}
        {canOrder && (
        <div className="space-y-3 mt-auto pt-4">
          {/* Quantity Controls or Add Button */}
          {currentQuantity > 0 ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={() => handleQuantityChange(currentQuantity - 1)}
                  className="w-8 h-8 rounded-full bg-secret-primary text-white hover:bg-secret-primary-dark transition-colors flex items-center justify-center"
                >
                  <Minus size={16} className="text-white" />
                </button>
                <span className="font-medium text-foreground min-w-[2rem] text-center">
                  {currentQuantity}
                </span>
                <button
                  type="button"
                  onClick={() => handleQuantityChange(currentQuantity + 1)}
                  disabled={currentQuantity >= actualRemaining}
                  className="w-8 h-8 rounded-full bg-secret-primary text-white hover:bg-secret-primary-dark transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus size={16} className="text-white" />
                </button>
              </div>
              <div className="text-secret-primary font-medium">
                {formatPrice(item.price * currentQuantity * 100)}
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleAddToCart}
              disabled={!isAvailable || isAdding}
              className="w-full secret-btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed text-white"
            >
              {isAdding ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Plus size={16} className="text-white" />
                  <span className="text-white">Add to Cart</span>
                </>
              )}
            </button>
          )}

        </div>
        )}

        {/* Per-Item Availability Display for Add-ons (Phase 3) */}
        {item.available_quantity !== null && item.available_quantity !== undefined && (
          <div className="mt-3 text-xs text-center text-muted-foreground">
            {actualRemaining > 0 ? `${actualRemaining} available` : 'Sold Out'}
          </div>
        )}
      </div>
    </div>
  )
}

export default MenuItemCard